import { Box, LinearProgress, Pagination, PaginationItem, Typography } from "@mui/material";
import { ReactNode } from "react";
import { DotLegalAlert, DotLegalButton, DotLegalFullScreenSpinner, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import AIDialogDisclaimer from "@common/components/AIDialogDisclaimer";
import ActivityForm from "../../../../annualWheel/activityForm/ActivityForm";
import { ObligationExtractionPanelState, useScrollToTopOnPageChange } from "./ObligationExtractionPanel.hooks";
import { ObligationDuplicateMatch } from "./ObligationExtraction.types";
import { getPaginationItemStyle, useObligationExtractionPanelStyles } from "./ObligationExtractionPanel.styles";

export interface IObligationExtractionPanelProps {
    state: ObligationExtractionPanelState;
    contractId: string;
    /** Closes the previewer and puts the user on the contract's obligations, where the new rows are. */
    onGoToObligations: () => void;
    /** The header offers the same rescan, so the dialog it opens is owned by the scope above both. */
    onOpenRescanDialog: () => void;
    /**
     * The entry points only guard the first scan. Every control in here that starts another one is a
     * fresh charge, so the panel has to know about the limit too.
     */
    isAiSpendingExhausted: boolean;
}

function ObligationExtractionPanel(props: IObligationExtractionPanelProps) {
    const { translateString } = useTranslation();
    const styles = useObligationExtractionPanelStyles();
    const state = props.state;
    const bodyRef = useScrollToTopOnPageChange(state.pageIndex, state.validationAttempt);

    // The shell's header is drawn above every one of these, so a state only has to supply its own middle.
    const renderStateShell = (content: ReactNode) => <Box sx={styles.centeredState}>{content}</Box>;

    if (state.hasScanFailed) {
        return renderStateShell(
            <>
                <DotLegalAlert severity="error" warningText={translateString("obligationExtractionFailed")} marginTop={0} marginBottom={0} />
                <DotLegalButton
                    buttonType="secondary"
                    // Wrapped rather than passed directly: scan takes optional guidance, and a bare
                    // reference would hand it the click event.
                    onClick={() => state.scan()}
                    disabled={props.isAiSpendingExhausted}
                    toolTip={props.isAiSpendingExhausted ? translateString("aiSpendingLimitReached") : undefined}
                >
                    {translateString("findObligationsWithAi")}
                </DotLegalButton>
            </>
        );
    }

    // The scan starts as soon as the panel opens, so "not started yet" and "running" are the same wait to
    // the user. Treating them as one state avoids a frame of idle UI before the request leaves.
    if (!state.hasScanned) {
        return renderStateShell(
            <>
                <Box>
                    <DotLegalFullScreenSpinner />
                </Box>
                <DotLegalHeader headerStyle={"medium"}>{translateString("obligationExtractionRunning")}</DotLegalHeader>
            </>
        );
    }

    if (state.pages.length === 0) {
        return renderStateShell(
            <>
                <DotLegalHeader headerStyle={"medium"}>{translateString("obligationExtractionNoneFound")}</DotLegalHeader>
                <DotLegalButton
                    buttonType="secondary"
                    onClick={props.onOpenRescanDialog}
                    disabled={props.isAiSpendingExhausted}
                    toolTip={props.isAiSpendingExhausted ? translateString("aiSpendingLimitReached") : undefined}
                >
                    {translateString("obligationExtractionScanAgain")}
                </DotLegalButton>
            </>
        );
    }

    // Placed after the empty check because a finished run still has pages, and before currentPage because
    // the completion screen replaces the review rather than decorating it.
    if (state.isTerminal) {
        const createdList = (
            <Box component="ul" sx={{ margin: 0, paddingLeft: 3 }}>
                {state.createdNames.map((name, index) => (
                    <Box component="li" key={`${name}-${index}`}>
                        {name}
                    </Box>
                ))}
            </Box>
        );

        return renderStateShell(
            <>
                {state.createdNames.length > 0 && (
                    <DotLegalAlert
                        severity="success"
                        warningHeader={translateString("obligationExtractionCreatedHeader", { count: state.createdNames.length })}
                        warningText={createdList}
                        marginTop={0}
                        marginBottom={0}
                        // The surrounding state shell centres its content, which leaves a list's wrapped
                        // lines floating away from their bullets.
                        sx={{ textAlign: "left" }}
                    />
                )}

                {state.counts.excluded > 0 && (
                    <Typography sx={styles.obligationStatusCounts}>{translateString("obligationsExcludedCount", { count: state.counts.excluded })}</Typography>
                )}

                {state.progress.failedPageIndex !== undefined ? (
                    <>
                        <DotLegalAlert
                            severity="error"
                            warningText={translateString("obligationExtractionCreateFailed", { name: state.failedName })}
                            marginTop={0}
                            marginBottom={0}
                        />
                        <Box sx={styles.footerActions}>
                            <DotLegalButton buttonType="secondary" size="small" onClick={state.backToReview}>
                                {translateString("obligationExtractionBackToReview")}
                            </DotLegalButton>
                            <DotLegalButton buttonType="primary" size="small" onClick={state.addObligations} disabled={state.outstandingCount === 0}>
                                {translateString("obligationExtractionTryAgain")}
                            </DotLegalButton>
                        </Box>
                    </>
                ) : (
                    <DotLegalButton buttonType="primary" onClick={props.onGoToObligations}>
                        {translateString("goToObligations")}
                    </DotLegalButton>
                )}
            </>
        );
    }

    const currentPage = state.currentPage;
    const createdSoFar = state.counts.created;
    const hasUnresolved = state.firstUnresolvedIndex !== undefined;

    // The pager collapses to an ellipsis once there are more obligations than fit, hiding most of the
    // per-number colours, so the counts are what actually scales. The outstanding count doubles as the
    // way to reach the first obligation still needing work.
    const renderSummary = () => {
        const segments: Array<ReactNode> = [];

        if (state.counts.ready > 0) {
            segments.push(
                <Typography sx={{ ...styles.obligationStatusCounts }} key="ready">
                    {translateString("obligationsReadyCount", { count: state.counts.ready })}
                </Typography>
            );
        }
        if (state.counts.created > 0) {
            segments.push(
                <Typography sx={{ ...styles.obligationStatusCounts }} key="created">
                    {translateString("obligationsCreatedCount", { count: state.counts.created })}
                </Typography>
            );
        }
        if (state.counts.excluded > 0) {
            segments.push(
                <Typography sx={{ ...styles.obligationStatusCounts }} key="excluded">
                    {translateString("obligationsExcludedCount", { count: state.counts.excluded })}
                </Typography>
            );
        }
        // Neutral and not clickable: these are simply not done yet, which is not a problem the user needs
        // taking to. Only the ones they have left behind get the red, actionable treatment.
        if (state.counts.untouched > 0) {
            segments.push(
                <Typography sx={{ ...styles.obligationStatusCounts }} key="untouched">
                    {translateString("obligationsToReviewCount", { count: state.counts.untouched })}
                </Typography>
            );
        }
        if (state.counts.needsAttention > 0) {
            segments.push(
                <Typography
                    sx={(theme) => ({
                        ...styles.obligationStatusCounts,
                        color: theme.palette.error.main,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                    })}
                    key="needsAttention"
                    onClick={() => state.goToPage(state.firstNeedsAttentionIndex!)}
                >
                    {translateString("obligationsNeedAttentionCount", { count: state.counts.needsAttention })}
                </Typography>
            );
        }

        return segments.flatMap((segment, index) =>
            index === 0
                ? [segment]
                : [
                      <Box key={`separator-${index}`} sx={styles.summarySeparator}>
                          &middot;
                      </Box>,
                      segment,
                  ]
        );
    };

    return (
        <>
            <Box>
                {/* The verdict describes the clause the model returned, so picking a different one in the
                    document retires it - there is nothing left to have matched. */}
                {!currentPage.clauseReplaced && currentPage.suggestion.duplicateMatch === ObligationDuplicateMatch.Clause && (
                    <DotLegalAlert severity="info" warningText={translateString("obligationSuggestionLikelyDuplicate")} marginTop={1} marginBottom={0} />
                )}
                {!currentPage.clauseReplaced && currentPage.suggestion.duplicateMatch === ObligationDuplicateMatch.Name && (
                    <DotLegalAlert severity="info" warningText={translateString("obligationSuggestionDuplicateName")} marginTop={1} marginBottom={0} />
                )}
                {!state.currentClauseLocated && (
                    <DotLegalAlert severity="warning" warningText={translateString("obligationSuggestionClauseNotLocated")} marginTop={1} marginBottom={0} />
                )}
            </Box>

            <Box ref={bodyRef} sx={currentPage.excluded ? { ...styles.body, ...styles.excludedBody } : styles.body}>
                <ActivityForm
                    model={currentPage.model}
                    onModelChange={state.onCurrentPageModelChange}
                    onScheduleChange={state.onCurrentPageScheduleChange}
                    showError={currentPage.showError}
                    firstInvalidSection={state.firstInvalidSection}
                    validationAttempt={state.validationAttempt}
                    contractInfo={state.contractInfo}
                    hideContractSourcePreviewActions
                />
            </Box>

            <Box sx={styles.footer}>
                {state.progress.isCreating ? (
                    <>
                        <Typography variant="body2">{translateString("creatingObligations")}</Typography>
                        <LinearProgress
                            variant="determinate"
                            value={state.progress.totalCount === 0 ? 0 : (state.progress.createdCount / state.progress.totalCount) * 100}
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="body2">
                            {translateString("obligationsCreatedProgress", { created: state.progress.createdCount, total: state.progress.totalCount })}
                        </Typography>
                    </>
                ) : (
                    <>
                        {state.progress.failedPageIndex !== undefined && (
                            <DotLegalAlert
                                severity="error"
                                // Both counts span the whole review, not the last run: createdSoFar is
                                // cumulative, so pairing it with that run's outstanding count reads
                                // "Created 3 of 2" on a second failure. failed is the pager number.
                                warningText={translateString("obligationsPartiallyCreated", {
                                    created: createdSoFar,
                                    total: state.includedCount,
                                    failed: state.progress.failedPageIndex + 1,
                                })}
                                marginTop={0}
                                marginBottom={0}
                            />
                        )}

                        <Box sx={styles.summary}>{renderSummary()}</Box>

                        <Box sx={styles.pager}>
                            <Pagination
                                count={state.pages.length}
                                page={state.pageIndex + 1}
                                onChange={(_event, page) => state.setPage(page)}
                                size="small"
                                siblingCount={1}
                                boundaryCount={1}
                                renderItem={(item) => (
                                    <PaginationItem {...item} sx={getPaginationItemStyle(item.page ? state.pageStatuses[item.page - 1] : undefined)} />
                                )}
                            />
                        </Box>

                        <Box sx={styles.footerActions}>
                            <DotLegalButton buttonType="secondary" size="small" onClick={state.toggleExcluded}>
                                {currentPage.excluded ? translateString("includeObligation") : translateString("excludeObligation")}
                            </DotLegalButton>

                            <Box sx={{ display: "flex", gap: 1 }}>
                                {state.pageIndex > 0 && (
                                    <DotLegalButton buttonType="secondary" size="small" onClick={state.goToPreviousPage}>
                                        {translateString("previous")}
                                    </DotLegalButton>
                                )}
                                {state.isLastPage ? (
                                    // Nothing left to post but something was created: the run is done, so the
                                    // footer offers the way onwards rather than a button that cannot act.
                                    state.outstandingCount === 0 && state.counts.created > 0 ? (
                                        <DotLegalButton buttonType="primary" size="small" onClick={props.onGoToObligations}>
                                            {translateString("goToObligations")}
                                        </DotLegalButton>
                                    ) : (
                                        <DotLegalButton
                                            buttonType="primary"
                                            size="small"
                                            onClick={state.addObligations}
                                            disabled={state.outstandingCount === 0}
                                            // Pressable while anything is outstanding so it can point at what is
                                            // missing. It refuses to create until every obligation is resolved.
                                            toolTip={hasUnresolved ? translateString("addObligationsBlockedTooltip") : undefined}
                                        >
                                            {translateString("addObligations")}
                                        </DotLegalButton>
                                    )
                                ) : (
                                    <DotLegalButton buttonType="primary" size="small" onClick={state.goToNextPage}>
                                        {translateString("next")}
                                    </DotLegalButton>
                                )}
                            </Box>
                        </Box>
                    </>
                )}

                <Box sx={styles.disclaimer}>
                    <AIDialogDisclaimer translationKey="aiChatDisclaimer" />
                </Box>
            </Box>
        </>
    );
}

export default ObligationExtractionPanel;
